import { create } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";
import { Game, GameStoreState, GameStoreActions, GameStatus } from "./types";
import { createConfiguredGame } from "./gameConfig";
import {
  createInitialGame,
  normalizePersistedGameState,
} from "./gamePersistence";

// Initial game state
const init = createInitialGame();

// Define store type
type GameStore = GameStoreState & GameStoreActions;

export const useGameStore = create<GameStore>()(
  persist<GameStore>(
    (set, get) => ({
      // State
      game: init,
      drawnIndex: -1,
      currentCard: undefined,
      localDrawnCards: [],

      // Actions
      configGame: (
        args: Partial<
          Pick<Game, "courts" | "players" | "perCourt" | "shortCourt">
        > = {},
      ) => {
        set(({ game }) => {
          return {
            game: createConfiguredGame(game, args),
            drawnIndex: -1,
            currentCard: undefined,
            localDrawnCards: [],
          };
        });
      },

      newGame: () => {
        set({ game: createInitialGame() });
        get().configGame();
      },

      unShareGame: () => {
        set((state) => ({
          game: {
            ...state.game,
            slug: null,
            roundId: undefined,
            syncStatus: "unsynced",
            updatedAt: Date.now(),
          },
        }));
      },

      setDrawnCard: (deckIndex: number) => {
        set((state) => {
          if (state.game.cards?.[deckIndex] === undefined) {
            console.error("No such card exists");
            return state;
          }

          // Add to local drawn cards if not already present
          const newLocalDrawnCards = state.localDrawnCards.includes(deckIndex)
            ? state.localDrawnCards
            : [...state.localDrawnCards, deckIndex];

          const localIndex = newLocalDrawnCards.indexOf(deckIndex);

          return {
            game: {
              ...state.game,
              lastDrawn: Math.max(state.game.lastDrawn, deckIndex),
              updatedAt: Date.now(),
            },
            currentCard: state.game.cards[deckIndex],
            drawnIndex: localIndex,
            localDrawnCards: newLocalDrawnCards,
          };
        });
      },

      drawCard: () => {
        const { game } = get();

        if (game.cards) {
          // Draw the next card from the shared deck (lastDrawn + 1)
          const nextIndex = game.lastDrawn + 1;
          if (game.cards[nextIndex]) {
            get().setDrawnCard(nextIndex);
          }
        }
      },

      navigateNext: () => {
        const {
          drawnIndex,
          localDrawnCards,
          game: { cards },
        } = get();

        // drawnIndex is now the local index, not deck index
        if (drawnIndex < localDrawnCards.length - 1) {
          const newLocalIndex = drawnIndex + 1;
          const deckIndex = localDrawnCards[newLocalIndex];
          set({
            currentCard: cards?.[deckIndex],
            drawnIndex: newLocalIndex,
          });
        }
      },

      navigateBack: () => {
        const {
          drawnIndex,
          localDrawnCards,
          game: { cards },
        } = get();

        // drawnIndex is now the local index, not deck index
        if (drawnIndex > 0) {
          const newLocalIndex = drawnIndex - 1;
          const deckIndex = localDrawnCards[newLocalIndex];
          set({
            currentCard: cards?.[deckIndex],
            drawnIndex: newLocalIndex,
          });
        }
      },

      hasGame: () => {
        return Boolean(get().game.cards?.length);
      },

      canPlay: () => {
        const { game } = get();
        return Boolean(game.cards?.length && !get().isComplete());
      },

      isPlaying: () => {
        const { game, localDrawnCards } = get();
        return Boolean(
          game.cards?.length &&
          localDrawnCards.length > 0 &&
          !get().isComplete(),
        );
      },

      isComplete: () => {
        const { cards, lastDrawn } = get().game;
        const cardsRemaining = cards?.length
          ? cards.length - (lastDrawn + 1)
          : 0;
        return Boolean(cards?.length && !cardsRemaining);
      },

      getGameStatus: (): GameStatus => {
        const store = get();
        return {
          hasGame: store.hasGame(),
          canPlay: store.canPlay(),
          isPlaying: store.isPlaying(),
          isComplete: store.isComplete(),
        };
      },

      setGame: (newGame: Game, options = {}) => {
        const currentState = get();
        const currentGame = currentState.game;
        const slugChanged =
          Boolean(currentGame.slug) && currentGame.slug !== newGame.slug;
        const roundChanged =
          Boolean(currentGame.slug) &&
          currentGame.slug === newGame.slug &&
          Boolean(newGame.roundId) &&
          currentGame.roundId !== newGame.roundId;
        const shouldClearLocalHistory =
          options.clearLocalHistory ||
          (!options.preserveLocalHistory && (slugChanged || roundChanged));

        if (shouldClearLocalHistory) {
          set({
            game: newGame,
            localDrawnCards: [],
            drawnIndex: -1,
            currentCard: undefined,
          });
        } else {
          const deckIndex =
            currentState.localDrawnCards[currentState.drawnIndex];
          set({
            game: newGame,
            currentCard:
              deckIndex === undefined ? undefined : newGame.cards?.[deckIndex],
          });
        }
      },

      setShortCourt: (court: number | undefined) => {
        set((state) => ({
          game: {
            ...state.game,
            shortCourt: court,
            updatedAt: Date.now(),
          },
        }));
      },

      setSyncStatus: (status: Game["syncStatus"]) =>
        set((state) => ({
          game: { ...state.game, syncStatus: status, updatedAt: Date.now() },
        })),

      addLocalDrawnCard: (index: number) => {
        set((state) => ({
          localDrawnCards: state.localDrawnCards.includes(index)
            ? state.localDrawnCards
            : [...state.localDrawnCards, index],
        }));
      },

      resetLocalDrawnCards: () => {
        set({
          localDrawnCards: [],
          drawnIndex: -1,
          currentCard: undefined,
        });
      },

      redrawCard: (cardIndex?: number) => {
        const { game, localDrawnCards, drawnIndex } = get();

        if (!game.cards || localDrawnCards.length === 0) {
          console.error("Cannot redraw: invalid state");
          return;
        }

        // Determine which card to redraw
        let currentCardIndex: number;
        if (cardIndex !== undefined) {
          // Validate that the provided cardIndex is in localDrawnCards
          if (!localDrawnCards.includes(cardIndex)) {
            console.error(
              `Cannot redraw: cardIndex ${cardIndex} is not in locally drawn cards`,
            );
            return;
          }
          currentCardIndex = cardIndex;
        } else {
          // Use current card (existing behavior)
          if (drawnIndex < 0) {
            console.error("Cannot redraw: no current card");
            return;
          }
          currentCardIndex = localDrawnCards[drawnIndex];
        }

        // Find remaining undrawn cards (indices after lastDrawn)
        const remainingCardIndices: number[] = [];
        for (let i = game.lastDrawn + 1; i < game.cards.length; i++) {
          remainingCardIndices.push(i);
        }

        if (remainingCardIndices.length === 0) {
          console.error("No undrawn cards available for redraw");
          return;
        }

        // Pick a random undrawn card
        const randomIndex = Math.floor(
          Math.random() * remainingCardIndices.length,
        );
        const swapCardIndex = remainingCardIndices[randomIndex];

        // Swap the cards in the deck
        const newCards = [...game.cards];
        const tempCard = newCards[currentCardIndex];
        newCards[currentCardIndex] = newCards[swapCardIndex];
        newCards[swapCardIndex] = tempCard;

        // Update the game state
        set((state) => ({
          game: {
            ...state.game,
            cards: newCards,
            updatedAt: Date.now(),
          },
          currentCard: newCards[currentCardIndex], // Update current card display
        }));
      },
    }),
    {
      name: "game-state",
      version: 2,
      migrate: (persistedState: unknown): Partial<GameStore> => {
        const normalized = normalizePersistedGameState(persistedState);
        markRecoveryNeeded(normalized.gameWasReset);
        return normalized.state;
      },
      merge: (persistedState: unknown, currentState: GameStore): GameStore => {
        const normalized = normalizePersistedGameState(persistedState);
        markRecoveryNeeded(normalized.gameWasReset);
        return { ...currentState, ...normalized.state };
      },
    } as PersistOptions<GameStore>,
  ),
);

function markRecoveryNeeded(recovered: boolean) {
  if (!recovered || typeof window === "undefined") return;
  if (!window.courtShuffle) window.courtShuffle = {};
  window.courtShuffle.gameRecoveryNeeded = true;
}
